import { motion } from "framer-motion";
import { Flame, Music, Dices, Users2, UtensilsCrossed } from "lucide-react";
import { BONFIRE_IMAGE, DRINKING_GAMES_IMAGE, ROOFTOP_IMAGE } from "@/data/site";

const items = [
  {
    title: "Bonfire Nights",
    desc: "Sit under the Himalayan sky, share stories, sip something warm.",
    icon: Flame,
    img: BONFIRE_IMAGE,
    span: "md:col-span-2 md:row-span-2",
  },
  {
    title: "Rooftop Sundowners",
    desc: "Acoustic sets, sunset cocktails and clouds rolling over the Himalayas.",
    icon: Music,
    img: ROOFTOP_IMAGE,
    span: "md:col-span-2",
  },
  {
    title: "Drinking Games",
    desc: "Beer pong, flip cup, dare cards — playful icebreakers, all wrapped before 9 PM.",
    icon: Dices,
    img: DRINKING_GAMES_IMAGE,
    span: "md:col-span-1 md:row-span-2",
  },
  {
    title: "Social Mixers",
    desc: "Sunset hikes & easy intros so you never have to break the ice alone.",
    icon: Users2,
    img: "https://images.unsplash.com/photo-1543007630-9710e4a00a20?auto=format&fit=crop&w=900&q=80",
    span: "md:col-span-1",
  },
  {
    title: "Community Dinners",
    desc: "Tibetan-inspired tasting menus over candle-lit long tables.",
    icon: UtensilsCrossed,
    img: "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=900&q=80",
    span: "md:col-span-2",
  },
];

export default function ExperiencesSection() {
  return (
    <section
      id="experiences"
      data-testid="experiences-section"
      className="relative py-24 md:py-32 px-4 sm:px-6 lg:px-8 bg-[#0a0a0a]"
    >
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-6">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] text-[#f5f0ea] font-semibold">
              The Nomikai experience
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 tracking-tighter text-white">
              Made for the people you{" "}
              <span className="text-[#A7D38B] italic">haven't</span> met yet.
            </h2>
          </div>
          <p className="text-neutral-400 max-w-sm">
            Every night here turns into a story. We design the moments — you
            collect the memories.
          </p>
        </div>

        {/* House-rule strip — sets Gangtok's peaceful vibe */}
        <div
          data-testid="house-rule-strip"
          className="mb-10 rounded-2xl glass border border-[#A7D38B]/20 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
        >
          <div className="flex items-center gap-2.5 text-[#A7D38B] flex-shrink-0">
            <span className="text-lg">🌙</span>
            <span className="text-[10px] uppercase tracking-[0.28em] font-semibold">
              House Rule
            </span>
          </div>
          <div className="hidden sm:block h-6 w-px bg-white/10" />
          <p className="text-sm text-neutral-200 leading-relaxed">
            <span className="text-white font-semibold">Quiet after 9 PM.</span>{" "}
            No loud parties, no neon, no noise — only conversations that go
            slow & deep.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-[200px] md:auto-rows-[220px] gap-4 md:gap-5">
          {items.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: i * 0.06 }}
              whileHover={{ scale: 1.005 }}
              className={`relative rounded-3xl overflow-hidden group border border-white/5 hover:border-[#A7D38B]/40 transition-colors ${it.span}`}
              data-testid={`experience-${it.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <img
                src={it.img}
                alt={it.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-5">
                <div className="inline-flex items-center gap-2 text-[#A7D38B] mb-2">
                  <it.icon size={16} />
                  <span className="text-[11px] uppercase tracking-[0.22em]">
                    {it.title}
                  </span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold leading-tight text-white">
                  {it.title}
                </h3>
                <p className="text-sm text-neutral-300 mt-1.5 max-w-md">
                  {it.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
